import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download as DownloadIcon, Upload as UploadIcon, Eye, FileText, XCircle, CheckCircle, Clock } from 'lucide-react';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getSortedRowModel,
    getPaginationRowModel
} from '@tanstack/react-table';
import { useNavigate, useParams } from "react-router-dom"
import axiosInstance, { portInUseForAPI } from "../../api/axiosInstance";
import Loading from "../../components/Loading";
import { CalculateTableArray } from "../../utils/Utils";
import { useTranslation } from 'react-i18next'
import PopupBanner from "../../components/PopupBanner";
const BulkPOManagement = () => {
    const { t } = useTranslation()
    const {
        ReviewDetails,
        BulkUpload,
        FileName,
        Status,
        UploadedBy,
        TotalRecords,
        ValidRecords,
        InvalidRecords,
        Download,
        SrNo,
        IDOpenRequest,
        PONumber,
        SKU,
        RequestType,
        CreatedBy,
        Error,
        Page,
        GoTO,
        Show,
        FileSuccessfullyDownloaded,
        NoActionTaken
    } = t('historyDetails')
    const { uploadId, vendorId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [sorting, setSorting] = useState([])
    const [detailsData, setDetailsData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [showCreateRequestModal, setshowCreateRequestModal] = useState(false)
    const [successmsg, setSuccessMsg] = useState("")
    const dataForValidDetails = useMemo(
        () => detailsData?.validList ?? [],
        [detailsData]
    );
    const dataForInvalidDetails = useMemo(
        () => detailsData?.inValidList ?? [], [detailsData]
    );
    const dataForNoActionTakenDetails = useMemo(
        () => detailsData?.noActionList ?? [], [detailsData]
    );

    const headers = {
        SRNO: t('historyDetails.SrNo'),
        IDOPENREQUEST: t('historyDetails.IDOpenRequest'),
        PONUMBER: t('historyDetails.PONumber'),
        SKU: t('historyDetails.SKU'),
        REQUESTTYPE: t('historyDetails.RequestType'),
        CREATEDBY: t('historyDetails.CreatedBy'),
        STATUS: t('historyDetails.Status'),
        ERROR: t('historyDetails.Error')
    }

    // Table columns
    const columnsForValidDetails = useMemo(
        () => [
            {
                header: headers.SRNO,
                accessorKey: 'uploadId',
                size: 60,
                minSize: 50,
                maxSize: 80,
                cell: ({ row }) => row.index + 1
            },
            {
                header: headers.IDOPENREQUEST,
                accessorKey: 'idOpenRequest',
                size: 300,
                minSize: 280,
                maxSize: 320,

            },
            {
                header: headers.PONUMBER,
                accessorKey: 'poNumber',
            },
            {
                header: headers.SKU,
                accessorKey: 'sku',
            },
            // {
            // 	header: 'Description',
            // 	accessorKey: 'description',
            // },
            {
                header: headers.REQUESTTYPE,
                accessorKey: 'requestType',
                cell: ({ row }) => {
                    const requestType = row.getValue("requestType");
                    return (
                        <span
                            className={`px-2 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-700`}
                        >
                            {requestType}
                        </span>
                    );
                }
            },
            {
                header: headers.CREATEDBY,
                accessorKey: 'createdBy',
            },
            {
                header: headers.STATUS,
                accessorKey: 'processingStatus',
                cell: ({ row }) => {
                    const status = row.getValue("processingStatus");


                    return (
                        <span
                            className={`px-2 py-0.5 rounded-full text-[10px] ${"bg-blue-100 text-blue-700"}`}
                        >
                            {status}
                        </span>
                    );
                }
            },
        ],
        [headers]
    );

    const columnsForInvalidDetails = useMemo(
        () => [
            {
                header: headers.SRNO,
                accessorKey: 'uploadId',
                size: 60,
                minSize: 50,
                maxSize: 80,
                cell: ({ row }) => row.index + 1
            },

            {
                header: headers.IDOPENREQUEST,
                accessorKey: 'idOpenRequest',
                size: 300,
                minSize: 280,
                maxSize: 320,
            },
            {
                header: headers.PONUMBER,
                accessorKey: 'poNumber',
            },
            {
                header: headers.SKU,
                accessorKey: 'sku',
            },
            // {
            //     header: 'Description',
            //     accessorKey: 'description',
            // },
            {
                header: headers.REQUESTTYPE,
                accessorKey: 'requestType',
                cell: ({ row }) => {
                    const requestType = row.getValue("requestType");

                    return (
                        <span
                            className={`px-2 py-0.5 rounded-full text-[10px] ${"bg-blue-100 text-blue-700"}`}
                        >
                            {requestType}
                        </span>
                    );
                }
            },
            {
                header: headers.CREATEDBY,
                accessorKey: 'createdBy',
            },
            {
                header: headers.STATUS,
                accessorKey: 'processingStatus',
                cell: ({ row }) => {
                    const status = row.getValue("processingStatus");
                    return (
                        <span
                            className={`px-2 py-0.5 rounded-full  text-[10px] ${"bg-red-100 text-red-700"}`}
                        >
                            {status}
                        </span>
                    );
                }
            },
            {
                header: headers.ERROR,
                accessorKey: 'errorMessage',
                cell: ({ row }) => {
                    const error = row.getValue("errorMessage");
                    return (
                        <span
                            className={`text-xs text-red-700`}
                        >
                            {error}
                        </span>
                    );
                }
            }
        ],
        [headers]
    );

    const tableData = () => {
        let dataForTable = []
        if (location.pathname.includes("no-action")) {
            dataForTable = dataForNoActionTakenDetails;
        } else if (location.pathname.includes("invalid")) {
            dataForTable = dataForInvalidDetails;
        } else {
            dataForTable = dataForValidDetails;
        }
        return dataForTable;
    }


    // React Table instance
    const table = useReactTable({
        data: tableData(),
        columns: location.pathname.includes("invalid") ? columnsForInvalidDetails : columnsForValidDetails,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const getDetailedData = () => {
        setLoading(true)
        axiosInstance.get(`${process.env.REACT_APP_API_DOMAIN}:${portInUseForAPI}/api/bulk-upload/details?uploadId=${uploadId}&vendorId=${vendorId}`
        ).then((res) => {
            setDetailsData(res?.data)
            setLoading(false)
        }).catch((err) => {
            setLoading(false)
        })
    }

    useEffect(() => {
        getDetailedData()
    }, [])
    const statusStyles = {
        PROCESSED: "bg-green-100 text-green-700",
        VALIDATED: "bg-yellow-100 text-yellow-700",
        FAILED: "bg-red-100 text-red-700",
        UPLOADED: "bg-blue-100 text-blue-700",
    };
    const user = JSON.parse(localStorage.getItem('userData'))
    const downloadErrorRecords = () => {
        setLoading(true)
        const payload = [];
        detailsData?.inValidList
            ?.forEach(element => {
                const data = {
                    "poNumber": element?.poNumber,
                    "idOpenRequest": element?.idOpenRequest,
                    "crType": element?.requestType,
                    "sku": element?.sku,
                    "errorReason": element?.errorMessage,
                    "supplierResponse": element?.supplierResponse || '',
                    "vendorSelectedSw": element?.vendorSelectedSw || '',
                    "reasonCodeShipWeek": element?.reasonCodeShipWeek || "",
                    "reasonCodeQty": element?.reasonCodeQty || "",
                    "reasonCodePrice": element?.reasonCodePrice || "",
                    "addComment": element?.addComment || "",
                    "reasonCodeDC": element?.reasonCodeDc || "",
                    "cargoReadySw": element?.cargoReadySw || "",
                    "bookingSw": element?.bookingSw || "",
                    "requestedDc": element?.requestedDc,


                }
                payload.push(data)
            })
        axiosInstance.post(
            `${process.env.REACT_APP_API_DOMAIN}:${portInUseForAPI}/api/download-invalid-records?userId=${detailsData?.uploadedBy}&vendorNumber=${detailsData?.vendorId}`,
            payload, {
            responseType: "blob", //for file downloads only
        }).then((response) => {
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;

            const name = user?.supplierName;
            const timestamp = Date.now();
            const fileName = `${name}${timestamp}.xlsx`;

            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            window.URL.revokeObjectURL(url);
            setLoading(false)
            setshowCreateRequestModal(true)
            const filesName = name + timestamp + ".xlsx"
            setSuccessMsg(
                <>
                    {FileSuccessfullyDownloaded}: <span style={{ color: 'green', fontWeight: 'bold' }}>{filesName}</span>
                </>
            );
        }).catch(async (error) => {
            let errorMessage = null
            try {
                if (!(error.response.data instanceof Blob)) {
                    throw new Error("Input is not a Blob");
                }
                const text = await error.response.data.text();   // Convert Blob → text
                errorMessage = JSON.parse(text)?.error ||  JSON.parse(text)?.message  
            } catch (err) {
                // If parsing fails, return raw text instead of throwing
                try {
                    const text = await error.response.data.text();
                    errorMessage = "An error encountered while processing the file"
                } catch {
                    errorMessage = "An error encountered while processing the file"
                }
            }
            setLoading(false)
            setshowCreateRequestModal(true)
            setSuccessMsg(
                <>
                    <span style={{ color: 'red', fontWeight: 'bold' }}>{errorMessage}</span>
                </>
            );
            setLoading(false)
        })

    }
    const closePopUpModal = () => {
        setshowCreateRequestModal(false)
    }


    return (
        <div class=" p-3 gap-6 font-pop">
            {!loading ? <><p class="text-xl  mb-6">{ReviewDetails}</p>

                <div className='w-full text-xs flex justify-start items-center gap-1 mb-4 font-medium text-gray-600'>
                    <p className='flex gap-1 items-center justify-center'>
                        <span
                            className='hover:underline cursor-pointer'
                            onClick={() => navigate('/vendor/bulk-upload')}
                        >
                            {BulkUpload}
                        </span>
                        <span>{'>'}</span>
                    </p>
                    <p className='flex gap-1 items-center justify-center'>
                        <span>{ReviewDetails}</span>
                    </p>
                </div>

                <div className="w-full mb-6">
                    <table className="w-full table-fixed text-left text-xs">
                        <thead>
                            <tr className="text-gray-600 font-medium border-b border-gray-200">
                                <th className="py-2 w-1/3">{FileName}</th>
                                <th className="py-2 w-1/3">{Status}</th>
                                <th className="py-2 w-1/3">{UploadedBy}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            <tr>
                                <td className="py-2">{detailsData?.fileName}</td>
                                <td className="py-2 "><span
                                    className={`px-2 py-0.5 rounded-full  text-[10px] ${statusStyles[detailsData?.status]}`}
                                >
                                    {detailsData?.status || ""}
                                </span></td>
                                <td className="py-2">{detailsData?.uploadedByName || "-"}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

                    {/* Card 1 - Total Records */}
                    <div className="flex justify-between items-center bg-white border rounded-md p-4 shadow-sm">
                        <div>
                            <p className="text-gray-500 text-sm">{TotalRecords}</p>
                            <p className="text-xl font-bold">{detailsData?.totalRecords}</p>
                        </div>
                        <FileText className="w-6 h-6 text-gray-600" />
                    </div>

                    {/* Card 2 - Valid Records */}
                    <div
                        onClick={() =>
                            navigate(`/vendor/bulk-upload/valid-records/${uploadId}/${vendorId}`)
                        }
                        className={`flex justify-between items-center bg-green-50 border rounded-md p-4 shadow-sm cursor-pointer hover:bg-green-100 transition 
        ${location.pathname.includes("invalid") ? "" : !location.pathname.includes("no-action") && "border-green-500"}`}
                    >
                        <div>
                            <p className="text-green-700 text-sm">{ValidRecords}</p>
                            <p className="text-xl font-bold text-green-900">
                                {detailsData?.validCount}
                            </p>
                        </div>
                        <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>

                    {/* Card 3 - Invalid Records */}
                    <div
                        onClick={() =>
                            navigate(`/vendor/bulk-upload/invalid-records/${uploadId}/${vendorId}`)
                        }
                        className={`flex justify-between items-center bg-red-50 border rounded-md p-4 shadow-sm cursor-pointer hover:bg-red-100 transition 
        ${location.pathname.includes("invalid") ? "border-red-500" : ""}`}
                    >
                        <div>
                            <p className="text-red-700 text-sm">{InvalidRecords}</p>
                            <p className="text-xl font-bold text-red-900">
                                {detailsData?.invalidCount}
                            </p>
                        </div>
                        <XCircle className="w-6 h-6 text-red-600" />
                    </div>

                    {/* Card 4 - No Action Taken */}
                    <div
                        onClick={() =>
                            navigate(`/vendor/bulk-upload/no-action/${uploadId}/${vendorId}`)
                        }
                        className={`flex justify-between items-center bg-blue-50 border rounded-md p-4 shadow-sm cursor-pointer hover:bg-blue-100 transition 
        ${location.pathname.includes("no-action") ? "border-blue-500" : ""}`}
                    >
                        <div>
                            <p className="text-blue-700 text-sm">{NoActionTaken}</p>
                            <p className="text-xl font-bold text-blue-900">
                                {detailsData?.noActionCount}
                            </p>
                        </div>
                        <Clock className="w-6 h-6 text-blue-600" />
                    </div>

                </div>


                <div className="p-1">
                    {/* <h4 class="text-medium font-semibold mb-4 mt-4">Valid Records</h4> */}
                    <div class="flex items-center justify-between bg-white  pt-4 pb-2">
                        {/* <!-- Left Title --> */}
                        <p class="text-sm font-semibold text-gray-800">
                            {location.pathname.includes("invalid") ? InvalidRecords : location.pathname.includes("no-action") ? "No Action Taken Records" : ValidRecords}
                        </p>

                        {/* <!-- Right Buttons --> */}
                        {location.pathname.includes("invalid") && <div class="flex items-center space-x-2">
                            {/* <!-- Download XLS Button --> */}
                            <button onClick={() => downloadErrorRecords()} disabled={dataForInvalidDetails?.length === 0} class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-500 rounded-md hover:bg-red-100 transition disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                                </svg>
                                {Download}
                            </button>

                            {/* <!-- Show All Button --> */}
                            {/* <button class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100">
                            Show All
                        </button> */}
                        </div>}
                    </div>
                    <div className="flex justify-end items-center gap-1 mb-2 text-xs cursor-pointer">
                        <button
                            className="border rounded px-1 py-0.5"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                        >
                            {'<<'}
                        </button>
                        <button
                            className="border rounded px-1 py-0.5 cursor-pointer"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            {'<'}
                        </button>
                        <button
                            className="border rounded px-1 py-0.5 cursor-pointer"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            {'>'}
                        </button>
                        <button
                            className="border rounded px-1 py-0.5 cursor-pointer"
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                        >
                            {'>>'}
                        </button>

                        <span className="min-w-max flex items-center gap-1">
                            {Page}{" "}
                            <strong>
                                {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                            </strong>
                        </span>

                        <span className="min-w-max flex items-center gap-1">
                            | {GoTO}
                            <input
                                type="number"
                                value={table.getState().pagination.pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0
                                    table.setPageIndex(page)
                                }}
                                className="border rounded px-1 py-0.5 w-12 text-xs"
                            />
                        </span>

                        <select
                            className="border rounded px-1 py-0.5 text-xs"
                            value={table.getState().pagination.pageSize}
                            onChange={(e) => {
                                table.setPageSize(Number(e.target.value))
                            }}
                        >
                            {CalculateTableArray(table?.getFilteredRowModel()?.rows?.length).map((pageSize) => (<option key={pageSize} value={pageSize}> {Show} {pageSize} </option>))}
                        </select>
                    </div>
                    <div className="w-full">
                        <div className="w-full overflow-x-auto">
                            <table className="text-xs border border-gray-300 w-full">
                                <thead className="bg-[#e0ecfc]">
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <tr key={headerGroup.id} className="divide-x-[1px] divide-gray-300">
                                            {headerGroup.headers.map((header) => (
                                                <th
                                                    key={header.id}
                                                    className="font-medium py-4 px-2 whitespace-nowrap overflow-hidden text-ellipsis text-center"
                                                    style={{ width: header.getSize() }}
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <div
                                                            {...{
                                                                className: header.column.getCanSort()
                                                                    ? 'cursor-pointer select-none'
                                                                    : '',
                                                                onClick: header.column.getToggleSortingHandler()
                                                            }}
                                                        >
                                                            <div className='flex justify-center h-5'>
                                                                {' '}
                                                                {/* Container with Flexbox */}
                                                                {/* <div className='text-center'> */}
                                                                {flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                                {/* </div> */}
                                                                &nbsp;&nbsp;
                                                                <div>
                                                                    {{
                                                                        asc: (
                                                                            <svg
                                                                                className='w-3 h-4 text-gray-500'
                                                                                // className="w-4 h-5 text-gray-500" // Increased size with w-4 and h-5
                                                                                aria-hidden='true'
                                                                                xmlns='http://www.w3.org/2000/svg'
                                                                                fill='none'
                                                                                viewBox='0 0 10 14'
                                                                            // viewBox="0 0 14 20" // Adjusted viewBox for larger arrow
                                                                            >
                                                                                <path
                                                                                    stroke='currentColor'
                                                                                    strokeLinecap='round'
                                                                                    strokeLinejoin='round'
                                                                                    strokeWidth='2'
                                                                                    d='M5 13V1m0 0L1 5m4-4 4 4'
                                                                                // d="M1 5h12m0 0-4 4m4-4L9 1"
                                                                                />
                                                                            </svg>
                                                                        ),
                                                                        desc: (
                                                                            <svg
                                                                                className='w-3 h-4 text-gray-500'
                                                                                // className="w-4 h-5 text-gray-500" // Increased size with w-4 and h-5
                                                                                aria-hidden='true'
                                                                                xmlns='http://www.w3.org/2000/svg'
                                                                                fill='none'
                                                                                viewBox='0 0 10 14'
                                                                            // viewBox="0 0 14 20" // Adjusted viewBox for larger arrow
                                                                            >
                                                                                <path
                                                                                    stroke='currentColor'
                                                                                    strokeLinecap='round'
                                                                                    strokeLinejoin='round'
                                                                                    strokeWidth='2'
                                                                                    d='M5 1v12m0 0 4-4m-4 4L1 9'
                                                                                // d="M13 5H1m0 0 4 4m-4-4 3-3m0 6V1m0 0 3 3m-3-3 3 3"
                                                                                />
                                                                            </svg>
                                                                        )
                                                                    }[header.column.getIsSorted()] ?? (
                                                                            <svg
                                                                                className='w-3 h-4 text-gray-500'
                                                                                aria-hidden='true'
                                                                                xmlns='http://www.w3.org/2000/svg'
                                                                                fill='none'
                                                                                viewBox='0 0 16 20'
                                                                            >
                                                                                <path
                                                                                    stroke='currentColor'
                                                                                    strokeLinecap='round'
                                                                                    strokeLinejoin='round'
                                                                                    strokeWidth='2'
                                                                                    d='M4 6v13m0 0 3-3m-3 3-3-3m11-2V1m0 0L9 4m3-3 3 3'
                                                                                // d="M4 6h10m0 0-3-3m3 3-3 3m1 8H1m0 0 3-3m-3 3 3 3"
                                                                                />
                                                                            </svg>
                                                                        )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                {table.getRowModel().rows.length > 0 ? <tbody>
                                    {table.getRowModel().rows.map((row, index) => (
                                        <tr
                                            key={row.id}
                                            className={`divide-x-[1px] divide-gray-300 ${index % 2 === 0 ? 'bg-[#ffffff]' : 'bg-[#ecedf2]'
                                                }`}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <td
                                                    key={cell.id}
                                                    className="px-2 py-2 text-center"
                                                    style={{ width: cell.column.getSize() }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody> : <tr>
                                    <td colspan="8" class="text-center py-4 text-black-600">
                                        No data available
                                    </td>
                                </tr>}
                            </table>
                        </div>
                    </div>
                </div></> : <Loading />}
            <PopupBanner
                isOpen={showCreateRequestModal}
                message={successmsg || "An error encountered while processing the file"}
                onCancel={closePopUpModal}

            />
        </div>
    )
}
export default BulkPOManagement